import { useRef } from 'react';

import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import type { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	SHARE_AND_RESTRICT_DIALOG_SAVE_ACTION_EXPERIENCE,
	SHARE_AND_RESTRICT_DIALOG_SHARE_ACTION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	ErrorFlagReason,
	GeneralAccessLevel,
	Principal,
	StagedPrincipalsAddMode,
	SuccessFlagReason,
	UserPickerPrincipal,
} from '../controlled/commonTypes.tsx';
import type { ConfluenceContentRestrictionState } from './mutations/__generated__/useSetContentGeneralAccessModeMutation.graphql.ts';
import type {
	ContentSubType,
	ContentType,
} from '../shareAndRestrictButton/ShareRestrictAndCopyCoordinator.tsx';

import { preloadSpaceRoleMode } from './preloadSpaceRoleMode.ts';
import type { MasterState, MasterStateSetter } from './masterState.ts';
import type { PrincipalsMap } from './principalsMap.ts';
import type {
	ContentRestrictionsMutator,
	NotificationsSender,
	ControllerExtras,
} from './Controller.tsx';

type CtaFlowEventFilter = CtaFlowEvent['type'][];
type CtaFlowEventAwaiter = (filter: CtaFlowEventFilter) => Promise<CtaFlowEvent>;
export type CtaFlowEventDispatcher = (event: CtaFlowEvent) => void;

/**
 * Basically all the data and functions the CTA Save handler could ever need for the
 * entire process.
 */
export type CtaSaveFlowEvent = {
	contentId: string;
	edition: ConfluenceEdition;
	numberOfAncestors: number;
	type: 'cta';
	addMode: StagedPrincipalsAddMode;
	mutateContentRestrictions: ContentRestrictionsMutator;
	mutateGeneralAccess?: ControllerExtras['mutateGeneralAccess'];
	mutateRestrictionsAccess?: ControllerExtras['mutateRestrictionsAccess'];
	currentUser: Principal;
	sendNotification: NotificationsSender;
	successFlag: (reason: SuccessFlagReason) => void;
	errorFlag: (reason: ErrorFlagReason) => void;
	beginTeardown: () => void;
	isMutatingAction: boolean;
	isNotifyingAction: boolean;
	userCanEditRestrictions: boolean;
	generalAccess: GeneralAccessLevel;
	principalsMap: PrincipalsMap;
	optionalMessage: string;
	principalsStagerPrincipals: UserPickerPrincipal[];
	stagedNotificationRecipients: Principal[];
	validateRestrictedPrincipals: any;
	experienceTracker: ExperienceTrackerAPI;
	fireAnalyticEvent: ControllerExtras['fireAnalyticEvent'];
	contentSubType?: ContentSubType;
	contentType: ContentType;
	callResolveMismatchedRestrictions: () => Promise<void>;
	isResolvingAction: boolean;
	isUnpublishedDraft: boolean;
	includeInvites: boolean;
	refreshDialogState?: () => Promise<void>;
	isGeneralAccessMutation?: boolean;
	isRestrictionsAccessMutation?: boolean;
};

export type CtaFlowEvent =
	| CtaSaveFlowEvent
	| { type: 'doNotifyGroups' }
	| { type: 'doNotNotifyGroups' }
	| { type: 'ignoreRestrictions'; shouldSkipFlagRaise: boolean }
	| { type: 'resolveRestrictionsComplete' };

export const useCtaFlowController = (setMasterState: MasterStateSetter): CtaFlowEventDispatcher => {
	const ref = useRef<null | CtaFlowEventDispatcher>(null);

	const { cloudId } = useSessionData();
	// Common React pattern for useRef taking a value instead of a value-creating
	// function
	function lazyGetFlowController(): CtaFlowEventDispatcher {
		const existingController = ref.current;
		if (existingController !== null) {
			return existingController;
		}

		const newController = startCtaFlow(setMasterState, cloudId);
		ref.current = newController;
		return newController;
	}

	return lazyGetFlowController();
};

/**
 * Creates a transmitter/receiver pair for events that the UI actions need to
 * give the flow controller
 */
function createEventChannel(): [CtaFlowEventAwaiter, CtaFlowEventDispatcher] {
	let awaiter: ((event: CtaFlowEvent) => void) | null = null;

	const dispatchEvent = (event: CtaFlowEvent) => {
		if (awaiter) {
			awaiter(event);
			awaiter = null;
		}
		// No awaiter? Drop the event. Let the user click again.
	};

	const awaitEvent = (filter: CtaFlowEventFilter): Promise<CtaFlowEvent> => {
		return new Promise((resolve) => {
			awaiter = (event: CtaFlowEvent) => {
				if (filter.includes(event.type)) {
					resolve(event);
				}
			};
		});
	};

	return [awaitEvent, dispatchEvent];
}

function startCtaFlow(setMasterState: MasterStateSetter, cloudId: string): CtaFlowEventDispatcher {
	// Build a channel for the flow to consume events
	const [awaitFlowEvent, dispatchFlowEvent] = createEventChannel();

	// Start the CTA flow
	void ctaFlowController(awaitFlowEvent, setMasterState, cloudId);

	// Give the sender a way to interact with it
	return dispatchFlowEvent;
}

/**
 * Determine if the flow should raise a flag.
 * If it should, identify which flag to raise.
 */
export function shouldRaiseFlag({
	flowEventType,
	isNotifyingAction,
	isMutatingAction,
	generalAccess,
	principalsMap,
	showWarnRestrictionsDialog,
	showResolveRestrictionsDialog,
	doNotNotifyGroups,
	userCanEditRestrictions,
	successFlag,
	isUnpublishedDraft,
}: {
	flowEventType: CtaFlowEvent['type'] | null;
	isNotifyingAction: boolean;
	isMutatingAction: boolean;
	generalAccess: CtaSaveFlowEvent['generalAccess'];
	principalsMap: CtaSaveFlowEvent['principalsMap'];
	showWarnRestrictionsDialog: boolean;
	showResolveRestrictionsDialog: boolean;
	doNotNotifyGroups: boolean;
	userCanEditRestrictions: boolean;
	successFlag: CtaSaveFlowEvent['successFlag'];
	isUnpublishedDraft: boolean;
}) {
	const isRestrictionsFlow = showWarnRestrictionsDialog || showResolveRestrictionsDialog;
	let areCanEditPrincipalsBeingAdded: boolean = false;
	const contentOrDraft = isUnpublishedDraft ? 'draft-shared' : 'content-shared';
	/**
	 * Check if any new principals are being added in this session.
	 * If yes, verify if any of them have the "can edit" role.
	 */
	const hasNewPrincipals = Array.from(principalsMap.values()).some((principal) => {
		const { isNewOrUpdatedPrincipal, role } = principal;
		if (isNewOrUpdatedPrincipal) {
			areCanEditPrincipalsBeingAdded = role === 'can-edit';
		}

		return isNewOrUpdatedPrincipal;
	});

	// Handle "notification" flows first
	if (isNotifyingAction) {
		const emailPrincipalsOnly = principalsMap.size === 0;

		if (isRestrictionsFlow) {
			if (flowEventType === 'ignoreRestrictions') {
				successFlag(contentOrDraft);
			} else if (flowEventType === 'resolveRestrictionsComplete') {
				successFlag('access-updated');
			}
			return;
		}

		// If no restrictions
		if (doNotNotifyGroups) {
			successFlag('access-updated');
			return;
		}

		// If only emails being notify
		if (emailPrincipalsOnly) {
			successFlag('notification-sent');
			return;
		}

		// Double-check if there are any other principals at this general access level. If there aren't, we are sending them an email notification
		if (
			(generalAccess === 'open-can-view' || generalAccess === 'restricted') &&
			!hasNewPrincipals
		) {
			successFlag('notification-sent');
			return;
		}

		// Check if the user is sharing but doesn't have permission to edit
		if (generalAccess === 'restricted' && !userCanEditRestrictions) {
			successFlag(contentOrDraft);
			return;
		}

		// Distinguish content-sharing from simple notification
		const message =
			generalAccess === 'open-can-edit' || generalAccess === 'open-can-view'
				? contentOrDraft
				: 'access-updated';
		successFlag(message);
		return;
	}

	// Otherwise handle "mutation" flows
	if (isMutatingAction) {
		if (isRestrictionsFlow) {
			if (flowEventType === 'ignoreRestrictions') {
				successFlag(isUnpublishedDraft ? 'draft-shared' : 'access-updated');
			} else if (flowEventType === 'resolveRestrictionsComplete') {
				successFlag('access-updated');
			}
			return;
		}

		// If new principals with the "can edit" role is added to this access level.
		if (areCanEditPrincipalsBeingAdded && hasNewPrincipals && generalAccess === 'open-can-view') {
			successFlag('access-updated');
			return;
		}

		// If principals are present and have access to this level.
		if (hasNewPrincipals) {
			if (generalAccess === 'open-can-edit' || generalAccess === 'open-can-view') {
				successFlag('has-access-already');
				return;
			}
		}

		// Default mutation scenario
		successFlag('access-updated');
	}
}

/**
 * The main flow control for the dialog. Assumes the state opens with the
 * main share dialog showing.
 * Exported strictly for testing, DO NOT USE in production code.
 *
 * What this controller actually controls is the process after clicking the "Save" CTA button
 * It involves showing things like the group notification confirmation dialog
 * Then the blocked access dialogues (resolve restrictions or warn)
 * Then sending some notifications etc.
 *
 * What id DOES NOT CONTROL are side experiences inside of USD like:
 * Public links flow
 * Copy link flow (which admittedly is just a button)
 * etc. There are likely more (like the upcoming stateful access requests list)
 *
 * The main point for writing all of this is to emphasize this controller
 * DOES NOT CONTROL EVERYTHING IN USD, just that very pivotal flow
 * that needs to happen after clicking the Save button. Cheers!
 */
export const ctaFlowController = async (
	awaitFlowEvent: CtaFlowEventAwaiter,
	setMasterState: MasterStateSetter,
	cloudId: string,
) => {
	while (true) {
		// In case we've re-entered this loop by a go-back from another dialog,
		// make sure the main shareAndRestrict mode is showing
		setMasterState((s: MasterState) => ({
			...s,
			isSaving: false,
			sessionState: 'shareAndRestrict',
		}));

		// Wait for the user to click the main dialog's CTA
		const {
			contentId,
			edition,
			numberOfAncestors,
			addMode,
			mutateContentRestrictions,
			currentUser,
			sendNotification,
			successFlag,
			errorFlag,
			beginTeardown,
			isMutatingAction,
			isNotifyingAction,
			userCanEditRestrictions,
			generalAccess,
			principalsMap,
			optionalMessage,
			stagedNotificationRecipients,
			experienceTracker,
			validateRestrictedPrincipals,
			fireAnalyticEvent,
			contentSubType,
			contentType,
			callResolveMismatchedRestrictions,
			isResolvingAction,
			isUnpublishedDraft,
			includeInvites,
			refreshDialogState,
			isGeneralAccessMutation = false,
			isRestrictionsAccessMutation = false,
			mutateGeneralAccess,
			mutateRestrictionsAccess,
		} = (await awaitFlowEvent(['cta'])) as CtaSaveFlowEvent;

		// Raise the loading spinner
		setMasterState((s: MasterState) => ({ ...s, isSaving: true }));

		if (isMutatingAction && contentType !== 'embed') {
			experienceTracker.start({
				name: SHARE_AND_RESTRICT_DIALOG_SAVE_ACTION_EXPERIENCE,
			});
		}

		// Figure out if the user elected to send notifications, and included groups
		const stagedNotificationRecipientsHasGroups = stagedNotificationRecipients.some(
			({ type }) => type === 'group' || type === 'team',
		);

		// If so, ask if they really intended to send notifications to all the group
		// members. Whittle down the final notification recipients list.
		let finalNotificationRecipients = stagedNotificationRecipients;
		// if we are going to send notification start the experience tracking
		if (isNotifyingAction && finalNotificationRecipients.length > 0) {
			experienceTracker.start({
				name: SHARE_AND_RESTRICT_DIALOG_SHARE_ACTION_EXPERIENCE,
			});
		}
		// In case user choses not to notify groups then the success flag will show
		//'access update' even though individual users still will be notified
		let doNotNotifyGroups = false;
		if (isNotifyingAction && stagedNotificationRecipientsHasGroups) {
			// End the saving state and switch dialogs
			setMasterState((s: MasterState) => ({
				...s,
				sessionState: 'confirmGroupNotifications',
				isSaving: false,
			}));

			// Await a button click
			const { type: flowEventType } = await awaitFlowEvent(['doNotifyGroups', 'doNotNotifyGroups']);

			// Filter the final list if they chose not to notify groups
			if (flowEventType === 'doNotNotifyGroups') {
				finalNotificationRecipients = stagedNotificationRecipients.filter(
					({ type }) => type !== 'group',
				);
				doNotNotifyGroups = true;
			}

			// Bring back the lockout
			setMasterState((s: MasterState) => ({ ...s, isSaving: true }));
		}

		if (isResolvingAction) {
			try {
				await callResolveMismatchedRestrictions();
			} catch (error) {
				fireAnalyticEvent({
					eventType: 'sendOperationalEvent',
					action: 'errored',
					actionSubject: 'resolveMismatchedRestrictions',
					actionSubjectId: 'flowController',
					attributes: { message: error.message },
				});

				errorFlag('mutation-error');
				experienceTracker.fail({
					name: SHARE_AND_RESTRICT_DIALOG_SAVE_ACTION_EXPERIENCE,
					error: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
					},
					attributes: {
						type: 'resolveMismatchedRestrictions',
					},
				});
				continue;
			}
		}

		// If there's a content restrictions mutation staged, kick it off now
		// We do not want to mutate, we only want to notify for smart links
		if (isMutatingAction && contentType !== 'embed' && !isResolvingAction) {
			const setGeneralAccessLoading = (isGeneralAccessLoading: boolean) => {
				setMasterState((s: MasterState) => ({
					...s,
					isGeneralAccessLoading,
				}));
			};

			const computeGeneralAccessDeltaFromMode = (
				mode: ConfluenceContentRestrictionState | null | undefined,
			): MasterState['generalAccessDelta'] => {
				switch (mode) {
					case 'VIEW_RESTRICTED':
						return 'restricted';
					case 'EDIT_RESTRICTED':
						return 'open-can-view';
					default:
						return 'open-can-edit';
				}
			};

			const fireMutationSucceeded = (
				actionSubject:
					| 'mutateGeneralAccess'
					| 'mutateRestrictionsAccess'
					| 'mutateContentRestrictions',
			) => {
				fireAnalyticEvent({
					eventType: 'sendOperationalEvent',
					action: 'succeeded',
					actionSubject,
					actionSubjectId: 'flowController',
					attributes: { generalAccess },
				});
			};

			type MutateGeneralAccess = NonNullable<typeof mutateGeneralAccess>;

			const mutateViaGeneralAccess = async (mutate: MutateGeneralAccess) => {
				setGeneralAccessLoading(true);
				try {
					const response = await mutate(generalAccess);
					const mode =
						response?.data?.confluence_setContentGeneralAccessMode?.contentGeneralAccess?.mode;
					const generalAccessDelta = computeGeneralAccessDeltaFromMode(mode);
					setMasterState((s: MasterState) => ({
						...s,
						generalAccessDelta,
					}));
				} finally {
					setGeneralAccessLoading(false);
				}

				fireMutationSucceeded('mutateGeneralAccess');
			};

			type MutateRestrictionsAccess = NonNullable<typeof mutateRestrictionsAccess>;

			const mutateViaRestrictionsAccess = async (mutate: MutateRestrictionsAccess) => {
				// TODO update includeInvites will be added later
				await mutate({
					principalsMap,
					includeInvites: false,
				});
				fireMutationSucceeded('mutateRestrictionsAccess');
			};

			const mutateViaContentRestrictions = async () => {
				await mutateContentRestrictions(generalAccess, principalsMap, currentUser, includeInvites);
				fireMutationSucceeded('mutateContentRestrictions');
			};

			const runRestrictionsMutation = async () => {
				if (
					isGeneralAccessMutation &&
					mutateGeneralAccess &&
					expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
				) {
					return mutateViaGeneralAccess(mutateGeneralAccess);
				}

				if (
					isRestrictionsAccessMutation &&
					mutateRestrictionsAccess &&
					expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
				) {
					return mutateViaRestrictionsAccess(mutateRestrictionsAccess);
				}

				return mutateViaContentRestrictions();
			};

			try {
				if (userCanEditRestrictions) {
					await runRestrictionsMutation();
				}

				experienceTracker.succeed({
					name: SHARE_AND_RESTRICT_DIALOG_SAVE_ACTION_EXPERIENCE,
				});
			} catch (error) {
				fireAnalyticEvent({
					eventType: 'sendOperationalEvent',
					action: 'errored',
					actionSubject: 'mutateContentRestrictions',
					actionSubjectId: 'flowController',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						generalAccess,
						userCanEditRestrictions,
						contentType,
						principalListSize: principalsMap.size,
						notificationRecipientsListSize: finalNotificationRecipients.length,
						userErrors: currentUser.type === 'user' && currentUser.knownErrors?.accessWarning,
						addMode,
						contentSubType,
					},
				});

				errorFlag('mutation-error');

				experienceTracker.fail({
					name: SHARE_AND_RESTRICT_DIALOG_SAVE_ACTION_EXPERIENCE,
					error,
					attributes: {
						type: 'mutateContentRestrictions',
						generalAccess,
						userCanEditRestrictions,
						contentType,
						principalListSize: principalsMap.size,
						notificationRecipientsListSize: finalNotificationRecipients.length,
						userErrors: currentUser.type === 'user' && currentUser.knownErrors?.accessWarning,
						addMode,
						contentSubType,
					},
				});

				// Don't take the flow any further; start another turn of the loop,
				// returning to the main dialog so the user can try again if they choose
				continue;
			}
		}

		// If there are notifications to be sent, do that now
		if (isNotifyingAction && finalNotificationRecipients.length > 0 && !isResolvingAction) {
			try {
				await sendNotification(finalNotificationRecipients, optionalMessage);

				experienceTracker.succeed({
					name: SHARE_AND_RESTRICT_DIALOG_SHARE_ACTION_EXPERIENCE,
				});
			} catch (error) {
				fireAnalyticEvent({
					eventType: 'sendOperationalEvent',
					action: 'errored',
					actionSubject: 'sendNotification',
					actionSubjectId: 'flowController',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						isLivePage: contentSubType === 'live',
						contentType,
						contentSubType,
						notificationRecipientsListSize: finalNotificationRecipients.length,
						userErrors: currentUser.type === 'user' && currentUser.knownErrors?.accessWarning,
						addMode,
						optionalMessage: optionalMessage !== '',
					},
				});
				if (error?.message === 'list-too-large-error') {
					errorFlag('list-too-large-error');
				} else {
					errorFlag('notification-error');
				}

				experienceTracker.fail({
					name: SHARE_AND_RESTRICT_DIALOG_SHARE_ACTION_EXPERIENCE,
					error,
					attributes: {
						type: 'sendNotification',
						isLivePage: contentSubType === 'live',
						contentType,
						contentSubType,
						notificationRecipientsListSize: finalNotificationRecipients.length,
						userErrors: currentUser.type === 'user' && currentUser.knownErrors?.accessWarning,
						addMode,
						optionalMessage: optionalMessage !== '',
					},
				});
			}
		}

		const stagedNotificationUsersOnly = stagedNotificationRecipients.filter((val) => {
			if (val.type == 'user') return val;
		});

		// validate if restricted access can be fixed and preload space role mode in parallel
		const [validationResult] = await Promise.all([
			validateRestrictedPrincipals({
				contentId,
				cloudId,
				edition,
				numberOfAncestors,
				addMode,
				stagedUserPrincipals: stagedNotificationUsersOnly,
				setMasterState,
				fireAnalyticEvent,
			}),
			preloadSpaceRoleMode(cloudId),
		]);

		const { showWarnRestrictionsDialog, showResolveRestrictionsDialog } = validationResult;

		let flowEventType: CtaFlowEvent['type'] | null = null;
		let skipFlagRaise: boolean = false;

		if (showWarnRestrictionsDialog) {
			setMasterState((s: MasterState) => ({
				...s,
				sessionState: 'warnRestrictedAccess',
				isSaving: false,
			}));

			// Await a button click
			await awaitFlowEvent(['ignoreRestrictions']);
		}

		if (showResolveRestrictionsDialog) {
			setMasterState((s: MasterState) => ({
				...s,
				sessionState: 'resolveRestrictions',
				isSaving: false,
			}));

			// Await a button click
			const flowEvent = await awaitFlowEvent(['ignoreRestrictions', 'resolveRestrictionsComplete']);
			const { type } = flowEvent;

			flowEventType = type;

			if (type === 'ignoreRestrictions') {
				const { shouldSkipFlagRaise } = flowEvent;
				skipFlagRaise = shouldSkipFlagRaise;
			}
		}

		if (!skipFlagRaise) {
			shouldRaiseFlag({
				flowEventType,
				isNotifyingAction,
				isMutatingAction,
				generalAccess,
				principalsMap,
				showWarnRestrictionsDialog,
				showResolveRestrictionsDialog,
				doNotNotifyGroups,
				userCanEditRestrictions,
				successFlag,
				isUnpublishedDraft,
			});
		}

		if (
			isRestrictionsAccessMutation &&
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
		) {
			// Continue the loop and the session after sharing or save a new restriction
			await refreshDialogState?.();
		} else if (
			isGeneralAccessMutation &&
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
		) {
			// Keep session alive without refetching after general access updates
			setMasterState((s: MasterState) => ({
				...s,
				isSaving: false,
			}));
		} else {
			// Break the loop and end the session
			beginTeardown();
			return;
		}
	}
};
